import React, { useEffect, useRef, useState } from 'react'
import { Button, CircularProgress, Link, Stack, Typography } from '@mui/material'
import { multiFactor, onAuthStateChanged, signInWithCustomToken, User } from 'firebase/auth'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { auth } from '../firebaseConfig'
import { setToken } from '../utils/auth'
import { useAuthStore } from '../store/authStore'
import { completeSignup, getSignupErrorDetail } from '../services/auth/signupSession'
import AuthShell from '../components/Auth/AuthShell'
import { loadTagManager, trackSignupConversion } from '../utils/tagManager'

// The SSO signup flow's own Firebase session (from signInWithPopup on the Signup page)
// persists across the full-page redirect to Stripe and back, but the SDK's rehydration
// from IndexedDB is asynchronous even for an already-signed-in user — auth.currentUser
// can't be trusted to already be populated at mount.
function waitForPersistedUser(): Promise<User | null> {
    return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe()
            resolve(user)
        })
    })
}

// Landing point for Stripe's Checkout success_url (?session_id=...). The account is
// created here, server-side, once /signup/complete confirms the Checkout Session — not
// in the Signup page, which only collected the OTP-verified email, name, and password.
const SignupComplete: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const sessionId = searchParams.get('session_id')
    // Set by create_sso_checkout_session's success_url — distinguishes the SSO completion
    // path (already signed in, no custom token needed) from the password flow below.
    const isSso = searchParams.get('method') === 'sso'
    const [error, setError] = useState<'expired' | 'generic' | 'already_completed' | null>(() =>
        sessionId ? null : 'generic'
    )
    // Bumped by handleRetry to re-run the completion effect below — not called directly,
    // so a retry gets the same effect-scoped cancellation guard as the initial attempt,
    // instead of a second, unguarded call racing whatever the first one is still doing.
    const [retryCount, setRetryCount] = useState(0)
    // Guards handleRetry against a fast double-click firing a second, real Checkout
    // completion (see handleRetry below) — POST /signup/complete is paid Stripe money,
    // not something to fire twice for no reason. Deliberately never checked inside the
    // completion effect itself, though: that could block a genuine second run under
    // Strict Mode, since a stale first run's own `cancelled` flag (below) already
    // silences its callback.
    const completingRef = useRef(false)
    const idToken = useAuthStore((state) => state.idToken)

    useEffect(loadTagManager, [])

    // Decoupled from however idToken ends up set (directly below, or from inside
    // MFATOTPSetupStep once enrollment succeeds) — this is the one place that reacts to it.
    useEffect(() => {
        if (idToken) navigate('/', { replace: true })
    }, [idToken, navigate])

    // A 'generic' failure (network blip, transient 500) doesn't mean the checkout itself
    // failed — Stripe may have already charged the card. Retrying must reuse the same
    // sessionId rather than sending the user through signup again, which could create a
    // second real Checkout Session for someone who already paid. Only a genuine 'expired'
    // session (backend-confirmed) has nothing left to retry.
    //
    // `cancelled` is a local variable scoped to each run, not the old component-lifetime
    // isMounted ref — a ref would carry its value across a dev-mode Strict Mode remount
    // and need a second effect just to reset it; a fresh variable just starts over.
    // retryCount exists so handleRetry can re-run this same effect (and get its own fresh
    // flag) instead of calling this logic directly, which would race whatever the first
    // call was still doing.
    useEffect(() => {
        if (!sessionId) return

        let cancelled = false
        completingRef.current = true

        completeSignup(sessionId)
            .then(async (completed) => {
                // Checked before signing in, not just before the bookkeeping after it:
                // signInWithCustomToken mutates Firebase's global auth singleton, a real
                // browser-wide side effect independent of this component — it must never
                // run once the page that requested it is gone.
                if (cancelled) return

                // Tracking must never fail a signup that has already succeeded.
                await trackSignupConversion(sessionId, completed.conversion).catch((err) => {
                    console.warn('[SignupComplete] Failed to report the signup conversion:', err)
                })
                if (cancelled) return

                if (isSso) {
                    // completeSignup was still called above — its Stripe-verification and
                    // completed_at bookkeeping matter regardless of auth method — but the
                    // custom_token it returns is discarded here: the browser is already
                    // signed in as this SSO identity (signInWithPopup, back on the Signup
                    // page), and swapping to a custom-token session would only lose the
                    // real first factor MFA enrollment needs, for no benefit.
                    const user = await waitForPersistedUser()
                    if (cancelled) return
                    if (!user) {
                        // Cleared storage or similar — no persisted session to hand off to.
                        setError('expired')
                        return
                    }
                    // Re-checked after this await too, not just before it: getIdToken() is its
                    // own async boundary, and setToken() is the same kind of browser-wide,
                    // component-independent side effect the comment above already calls out —
                    // it must never run once the page that requested it is gone either.
                    const idToken = await user.getIdToken()
                    if (cancelled) return
                    setToken(idToken)
                    return
                }

                const userCredential = await signInWithCustomToken(auth, completed.custom_token)
                const user = userCredential.user

                // From this point on, signInWithCustomToken has already mutated Firebase's
                // global auth singleton — a real, app-wide side effect that outlives this
                // component. App.tsx's useFirebaseTokenListener reacts to that mutation on
                // its own, independent of whether this page is still mounted, so every
                // `cancelled` check below must sign back out rather than just skip the
                // local React updates.
                if (cancelled) {
                    await auth.signOut()
                    return
                }

                if (multiFactor(user).enrolledFactors.length > 0) {
                    // Shouldn't happen for a signup this fresh, but stays consistent with
                    // the normal login gate rather than assuming it never will — a retried
                    // completion within COMPLETE_RETRY_TTL could land here after an earlier
                    // attempt already enrolled one.
                    const freshIdToken = (await user.getIdTokenResult()).token
                    if (cancelled) {
                        await auth.signOut()
                        return
                    }
                    setToken(freshIdToken)
                    return
                }

                if (cancelled) {
                    await auth.signOut()
                    return
                }
                // Identity Platform rejects mfaEnrollment:start for a custom-token session
                // with UNSUPPORTED_FIRST_FACTOR — enrolling a new MFA factor is only allowed
                // when the session's first factor is password (or phone/federated), never a
                // custom token (confirmed live against a real test-mode signup, not a config
                // issue). The password-login flow already enrolls TOTP correctly, since it's
                // a real password first factor — route a fresh signup through that instead of
                // trying to enroll here.
                await auth.signOut()
                // Checked once more here, not just before the call above: the page can be
                // abandoned while this signOut() itself is still pending, and navigating a
                // component that's already unmounted would redirect a page the user has
                // already moved on from back to login.
                if (cancelled) return
                navigate('/?signup=mfa_required', { replace: true })
            })
            .catch((err) => {
                console.warn('[SignupComplete] Failed to complete signup:', err)
                if (cancelled) return
                const detail = getSignupErrorDetail(err)
                if (detail === 'signup_expired' || detail === 'payment_not_completed') {
                    setError('expired')
                } else if (detail === 'already_completed') {
                    // The account exists but this session_id is past its short retry
                    // window — retrying here would just hit the same wall again, so the
                    // only useful action is signing in normally.
                    setError('already_completed')
                } else {
                    setError('generic')
                }
            })
            .finally(() => {
                // Not reset if this run was already superseded (Strict Mode's stale first
                // run, or one this component outlived) — the run that's still current is
                // what owns clearing it.
                if (!cancelled) completingRef.current = false
            })

        return () => {
            cancelled = true
        }
    }, [sessionId, retryCount, navigate, isSso])

    const handleRetry = () => {
        // No sessionId at all (not even a malformed one) means there's nothing a retry
        // could ever do with it — same as 'expired', not a transient failure worth
        // clearing the error for. Without this check, clearing the error here would leave
        // the effect above's own early return with nothing to show: no spinner progress,
        // no error UI, no way back.
        if (error === 'expired' || !sessionId) {
            navigate('/signup')
            return
        }
        if (error === 'already_completed') {
            navigate('/')
            return
        }
        // Checked and set right here, inline — two clicks in the same batch both run this
        // handler before React commits anything, so a flag only the effect below sets
        // would still read false for both.
        if (completingRef.current) return
        completingRef.current = true
        setError(null)
        setRetryCount((count) => count + 1)
    }

    return (
        <AuthShell>
            <Stack justifyContent="center" alignItems="center" width={400} p={4} spacing="16px">
                {!error && (
                    <>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t('signup.completing')}
                        </Typography>
                    </>
                )}
                {error && (
                    <>
                        <Typography variant="subtitle2" color="error" align="center">
                            {error === 'expired'
                                ? t('signup.complete_expired')
                                : error === 'already_completed'
                                  ? t('signup.already_completed')
                                  : t('signup.error_generic')}
                        </Typography>
                        <Button variant="contained" color="primary" onClick={handleRetry}>
                            {error === 'already_completed' ? t('signup.go_to_login') : t('signup.retry_button')}
                        </Button>
                        {error === 'generic' && (
                            <Link component={RouterLink} to="/" variant="body2">
                                {t('signup.go_to_login')}
                            </Link>
                        )}
                    </>
                )}
            </Stack>
        </AuthShell>
    )
}

export default SignupComplete
